import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Shield, Star, Users, Wrench, Home, ShoppingCart, Building, MessageSquare, Sparkles, ArrowRight, CheckCircle, Tag, MapPin, Clock } from 'lucide-react';
import api from '../utils/api';
import LandingTabs from '../components/Landing/LandingTabs';

const FEATURES = [
  { icon: Search, title: 'Trova Professionisti', desc: 'Cerca idraulici, elettricisti, muratori e tanti altri nella tua zona con recensioni verificate.', color: '#10B981' },
  { icon: ShoppingCart, title: 'Marketplace', desc: 'Compra e vendi materiali, mobili e attrezzature per la casa tra privati e professionisti.', color: '#3B82F6' },
  { icon: Building, title: 'Immobili', desc: 'Trova case in vendita o in affitto con ricerca avanzata per zona, prezzo e caratteristiche.', color: '#8B5CF6' },
  { icon: Tag, title: 'Promozioni', desc: 'Scopri le offerte speciali dei professionisti della tua zona. Risparmia sui lavori di casa.', color: '#F59E0B' },
  { icon: MessageSquare, title: 'Messaggi Diretti', desc: 'Contatta i professionisti direttamente, richiedi preventivi e gestisci le conversazioni.', color: '#EF4444' },
  { icon: Sparkles, title: 'Assistente AI', desc: 'Chiedi consigli su ristrutturazioni, materiali, costi e normative al nostro assistente intelligente.', color: '#06B6D4' },
];

const STEPS = [
  { num: '01', title: 'Registrati Gratis', desc: 'Crea il tuo account in pochi secondi, anche con Google.' },
  { num: '02', title: 'Cerca o Pubblica', desc: 'Trova professionisti, annunci e immobili oppure pubblica le tue richieste.' },
  { num: '03', title: 'Contatta e Collabora', desc: 'Parla direttamente con i professionisti e gestisci tutto dalla piattaforma.' },
];

const LandingPage = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);

  const trackCta = (buttonId) => {
    api.post('/api/track/cta', { button_id: buttonId, page: 'landing' }).catch(() => {});
  };

  useEffect(() => {
    api.get('/api/public/stats').then(r => setStats(r.data)).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-white" data-testid="landing-page">
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-[#0F1729]" data-testid="hero-section">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-0 left-0 w-96 h-96 bg-[#10B981] rounded-full filter blur-[120px] -translate-x-1/2 -translate-y-1/2" />
          <div className="absolute bottom-0 right-0 w-80 h-80 bg-[#F59E0B] rounded-full filter blur-[100px] translate-x-1/3 translate-y-1/3" />
        </div>
        <div className="relative max-w-6xl mx-auto px-5 pt-3 pb-6 sm:pt-4 sm:pb-8 lg:pt-5 lg:pb-10">
          {/* Mobile: logo sopra, Desktop: testo sx + logo dx */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:gap-8">
            {/* Testo */}
            <div className="order-2 lg:order-1 lg:flex-1 max-w-2xl">
              <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-1.5 rounded-full mb-3">
                <span className="w-2 h-2 rounded-full bg-[#10B981] animate-pulse" />
                <span className="text-sm text-white/80 font-medium">La piattaforma per la tua casa</span>
              </div>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white leading-[1.05] tracking-tight mb-3">
                Tutto per la tua <span className="text-[#10B981]">casa</span>,{' '}
                <span className="text-[#F59E0B]">un'unica</span> piattaforma.
              </h1>
              <p className="text-sm sm:text-base text-slate-300 max-w-xl mb-4 leading-relaxed">
                Trova professionisti affidabili, scopri annunci, esplora immobili e usa l'AI per i tuoi progetti di casa. Tutto in MakeHome 360.
              </p>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => { trackCta('hero_inizia_gratis'); navigate('/register'); }}
                  className="px-5 py-2.5 bg-[#10B981] hover:bg-[#059669] text-white font-bold rounded-xl text-sm transition-all transform hover:scale-[1.02] flex items-center gap-2 shadow-lg shadow-[#10B981]/20"
                  data-testid="hero-register-btn"
                >
                  Inizia Gratis <ArrowRight className="w-4 h-4" />
                </button>
                <button
                  onClick={() => { trackCta('hero_accedi'); navigate('/login'); }}
                  className="px-5 py-2.5 bg-white/10 hover:bg-white/15 backdrop-blur-sm text-white font-bold rounded-xl text-sm transition-all border border-white/20"
                  data-testid="hero-login-btn"
                >
                  Accedi
                </button>
                <button
                  onClick={() => { trackCta('hero_ospite'); navigate('/search'); }}
                  className="px-5 py-2.5 bg-white/5 hover:bg-white/10 backdrop-blur-sm text-white/90 font-semibold rounded-xl text-sm transition-all border border-white/10"
                  data-testid="hero-ospite-btn"
                >
                  Entra come ospite
                </button>
                <button
                  onClick={() => { trackCta('hero_rivista'); navigate('/rivista'); }}
                  className="px-5 py-2.5 bg-white/5 hover:bg-white/10 backdrop-blur-sm text-white/80 font-semibold rounded-xl text-sm transition-all border border-white/10"
                  data-testid="hero-rivista-btn"
                >
                  La Rivista
                </button>
              </div>
              {/* Stats — compact horizontal */}
              <div className="flex flex-wrap items-center gap-4 sm:gap-6 mt-4">
                {[
                  { value: stats ? stats.total_businesses.toLocaleString('it-IT') : '...', label: 'Aziende' },
                  { value: stats ? `${stats.total_categories}` : '...', label: 'Categorie' },
                  { value: stats ? `${stats.total_subcategories}` : '...', label: 'Sotto-cat.' },
                  { value: stats ? stats.total_cities.toLocaleString('it-IT') : '...', label: 'Città' },
                ].map(stat => (
                  <div key={stat.label} className="flex items-baseline gap-1.5">
                    <p className="text-xl sm:text-2xl font-black text-white">{stat.value}</p>
                    <p className="text-[10px] sm:text-xs text-slate-400 uppercase tracking-wider">{stat.label}</p>
                  </div>
                ))}
              </div>
            </div>
            {/* Logo */}
            <div className="order-1 lg:order-2 lg:flex-shrink-0 mb-1 lg:mb-0">
              <img
                src="https://customer-assets.emergentagent.com/job_makehome-frontend/artifacts/pnxu5fqe_6.png"
                alt="MakeHome 360"
                className="w-72 h-72 sm:w-80 sm:h-80 lg:w-[30rem] lg:h-[30rem] object-contain"
                data-testid="hero-logo"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Tabs Switcher: Trova Pro / AI Studio / Crea Richiesta / Richieste per te */}
      <LandingTabs />

      {/* Categories - from real DB */}
      <section className="py-12 sm:py-16 bg-gray-50" data-testid="categories-section">
        <div className="max-w-6xl mx-auto px-5">
          <h2 className="text-base sm:text-lg font-bold text-[#1E293B] mb-6 text-center">Trova il professionista giusto</h2>
          <div className="flex flex-wrap justify-center gap-2.5">
            {(stats?.top_categories || []).map(cat => (
              <button
                key={cat.name}
                onClick={() => navigate(`/professionals?category=${encodeURIComponent(cat.name)}`)}
                className="px-4 py-2.5 bg-white text-sm font-semibold text-[#1E293B] rounded-xl border border-gray-200 hover:border-[#10B981] hover:text-[#10B981] hover:shadow-sm transition-all"
              >
                {cat.name} <span className="text-xs text-gray-400 ml-1">({cat.count.toLocaleString()})</span>
              </button>
            ))}
            {!stats && ['Idraulico', 'Elettricista', 'Muratore', 'Imbianchino', 'Falegname', 'Fabbro', 'Giardiniere', 'Architetto'].map(cat => (
              <button key={cat} className="px-4 py-2.5 bg-white text-sm font-semibold text-[#1E293B] rounded-xl border border-gray-200 animate-pulse">{cat}</button>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Businesses from DB */}
      {stats?.featured?.length > 0 && (
        <section className="py-12 sm:py-16" data-testid="featured-section">
          <div className="max-w-6xl mx-auto px-5">
            <h2 className="text-base sm:text-lg font-bold text-[#1E293B] mb-2 text-center">Professionisti in evidenza</h2>
            <p className="text-sm text-gray-500 text-center mb-8">Le migliori aziende sul nostro database</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {stats.featured.map((biz, i) => (
                <div key={i} onClick={() => navigate(`/professionals/${biz.professional_id}`)} className="bg-white rounded-xl border border-gray-100 overflow-hidden hover:shadow-lg transition-all group cursor-pointer" data-testid={`featured-biz-${i}`}>
                  {biz.thumbnail ? (
                    <img src={biz.thumbnail} alt={biz.company_name} className="w-full h-32 object-cover group-hover:scale-105 transition-transform" onError={(e) => { e.target.style.display='none'; e.target.nextSibling.style.display='flex'; }} />
                  ) : null}
                  <div className={`w-full h-32 bg-gradient-to-br from-[#10B981]/10 to-[#3B82F6]/10 items-center justify-center ${biz.thumbnail ? 'hidden' : 'flex'}`}>
                    <span className="text-2xl font-bold text-gray-300">{(biz.company_name || biz.business_name)?.charAt(0)}</span>
                  </div>
                  <div className="p-3">
                    <h3 className="font-bold text-sm text-[#1E293B] truncate">{biz.company_name}</h3>
                    <div className="flex items-center gap-1.5 mt-1">
                      {biz.city && <span className="text-[10px] text-gray-500 flex items-center gap-0.5"><MapPin className="w-2.5 h-2.5" />{biz.city}</span>}
                      {biz.rating && <span className="text-[10px] text-yellow-600 font-bold flex items-center gap-0.5"><Star className="w-2.5 h-2.5 fill-yellow-500" />{biz.rating}</span>}
                    </div>
                    {biz.categories?.length > 0 && (
                      <p className="text-[9px] text-blue-600 mt-1 truncate">{biz.categories[0]?.sub_label}</p>
                    )}
                    {biz.opening_hours && (
                      <p className="text-[9px] text-gray-400 mt-1 flex items-center gap-0.5 truncate"><Clock className="w-2.5 h-2.5 flex-shrink-0" />{biz.opening_hours}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Features */}
      <section className="py-16 sm:py-20" data-testid="features-section">
        <div className="max-w-6xl mx-auto px-5">
          <div className="text-center mb-12">
            <h2 className="text-base sm:text-lg font-bold text-[#1E293B] mb-2">Cosa puoi fare con MakeHome 360</h2>
            <p className="text-sm text-gray-500 max-w-md mx-auto">Una piattaforma completa per ogni esigenza legata alla casa</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {FEATURES.map(feat => (
              <div key={feat.title} className="group p-6 rounded-2xl border border-gray-100 hover:border-gray-200 hover:shadow-lg transition-all bg-white">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4" style={{ backgroundColor: `${feat.color}15` }}>
                  <feat.icon className="w-6 h-6" style={{ color: feat.color }} />
                </div>
                <h3 className="font-bold text-[#1E293B] mb-2">{feat.title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed">{feat.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-16 sm:py-20 bg-[#0F1729]" data-testid="how-it-works-section">
        <div className="max-w-6xl mx-auto px-5">
          <div className="text-center mb-12">
            <h2 className="text-base sm:text-lg font-bold text-white mb-2">Come Funziona</h2>
            <p className="text-sm text-slate-400">Inizia in 3 semplici passaggi</p>
          </div>
          <div className="grid sm:grid-cols-3 gap-6 max-w-3xl mx-auto">
            {STEPS.map(step => (
              <div key={step.num} className="text-center">
                <div className="w-14 h-14 rounded-2xl bg-[#10B981]/10 flex items-center justify-center mx-auto mb-4">
                  <span className="text-[#10B981] font-black text-lg">{step.num}</span>
                </div>
                <h3 className="font-bold text-white mb-2">{step.title}</h3>
                <p className="text-sm text-slate-400 leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* For Professionals */}
      <section className="py-16 sm:py-20" data-testid="pro-section">
        <div className="max-w-6xl mx-auto px-5">
          <div className="bg-gradient-to-br from-[#1E293B] to-[#0F172A] rounded-3xl p-8 sm:p-12 flex flex-col lg:flex-row items-center gap-8">
            <div className="flex-1">
              <div className="inline-flex items-center gap-2 bg-[#F59E0B]/10 px-3 py-1 rounded-full mb-4">
                <Shield className="w-3.5 h-3.5 text-[#F59E0B]" />
                <span className="text-xs font-bold text-[#F59E0B]">PER PROFESSIONISTI</span>
              </div>
              <h2 className="text-base sm:text-lg font-bold text-white mb-3">Fai crescere la tua attivit&agrave;</h2>
              <p className="text-sm text-slate-300 leading-relaxed mb-6">
                Crea il tuo profilo professionale, ricevi richieste di lavoro, gestisci preventivi con il CRM integrato e promuovi i tuoi servizi con l'AI.
              </p>
              <ul className="space-y-3 mb-6">
                {['Profilo professionale verificato', 'CRM per gestire clienti e preventivi', 'Promozioni con AI Promo Wizard', 'Catalogo prodotti e servizi'].map(item => (
                  <li key={item} className="flex items-center gap-2.5 text-sm text-slate-300">
                    <CheckCircle className="w-4 h-4 text-[#10B981] flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
              <button
                onClick={() => { trackCta('pro_registrati'); navigate('/register'); }}
                className="px-6 py-3 bg-[#F59E0B] hover:bg-[#D97706] text-[#1E293B] font-bold rounded-xl text-sm transition-all"
                data-testid="pro-register-btn"
              >
                Registrati come Professionista
              </button>
            </div>
            <div className="flex-shrink-0 hidden lg:flex">
              <div className="w-64 h-64 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center">
                <Wrench className="w-24 h-24 text-[#10B981]/30" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 sm:py-20 bg-gray-50" data-testid="cta-section">
        <div className="max-w-2xl mx-auto px-5 text-center">
          <h2 className="text-base sm:text-lg font-bold text-[#1E293B] mb-3">Pronto a iniziare?</h2>
          <p className="text-sm text-gray-500 mb-8">Unisciti a MakeHome 360 e scopri il modo migliore per gestire tutto ci&ograve; che riguarda la tua casa.</p>
          <div className="flex flex-wrap justify-center gap-3">
            <button
              onClick={() => { trackCta('cta_crea_account'); navigate('/register'); }}
              className="px-8 py-3.5 bg-[#10B981] hover:bg-[#059669] text-white font-bold rounded-xl text-sm transition-all flex items-center gap-2"
              data-testid="cta-register-btn"
            >
              Crea Account Gratis <ArrowRight className="w-4 h-4" />
            </button>
            <button
              onClick={() => { trackCta('cta_ho_account'); navigate('/login'); }}
              className="px-8 py-3.5 bg-[#1E293B] hover:bg-[#334155] text-white font-bold rounded-xl text-sm transition-all"
              data-testid="cta-login-btn"
            >
              Ho gi&agrave; un account
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0F1729] py-8 text-center" data-testid="landing-footer">
        <div className="max-w-6xl mx-auto px-5">
          <div className="flex items-center justify-center mb-3">
            <img
              src="https://customer-assets.emergentagent.com/job_makehome-frontend/artifacts/pnxu5fqe_6.png"
              alt="MakeHome 360"
              className="w-20 h-20 sm:w-24 sm:h-24 object-contain"
              data-testid="footer-logo"
            />
          </div>
          <p className="text-xs text-slate-500">&copy; {new Date().getFullYear()} MakeHome 360. Tutti i diritti riservati.</p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
